/**
 * ChatSessionContext
 *
 * Persistent chat state that lives above the route hierarchy.
 * useTalkSession() and useVoiceChat() are called once here so
 * messages survive pane/project navigation.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useProject } from './ProjectContext';
import { useTalkSession, type TalkMessage, type UseTalkSessionResult } from '../hooks/useTalkSession';
import { useVoiceChat, type UseVoiceChatResult } from '../hooks/useVoiceChat';
import { useChatSelection } from './ChatSelectionContext';
import { getChatHintsForPath } from '../shared/route-registry';

const PANEL_EXPANDED_KEY = 'martha_chat_sidebar_expanded';
const PANEL_WIDTH_KEY = 'martha_chat_sidebar_width';

function loadExpanded(): boolean {
  try {
    return localStorage.getItem(PANEL_EXPANDED_KEY) === 'true';
  } catch {
    return false;
  }
}

function saveExpanded(v: boolean): void {
  try {
    localStorage.setItem(PANEL_EXPANDED_KEY, String(v));
  } catch { /* ignore */ }
}

export interface ChatSessionContextValue {
  // Panel state
  isExpanded: boolean;
  setExpanded: (v: boolean) => void;
  toggle: () => void;

  // Talk session
  messages: TalkMessage[];
  isStreaming: boolean;
  sendMessage: (content: string, options?: { agentMode?: boolean }) => void;
  cancelStream: () => void;
  sessionId: string | null;
  sessions: UseTalkSessionResult['sessions'];
  loadSession: (id: string) => void;
  startNewSession: () => void;
  archiveSession: (id: string) => void;
  isLoadingSessions: boolean;
  queuedMessage: string | null;
  clearQueue: () => void;

  // Voice
  voiceStatus: UseVoiceChatResult['voiceStatus'];
  isVoiceActive: boolean;
  startVoice: () => void;
  stopVoice: () => void;
  playTts: (text: string) => void;
  isTtsPlaying: boolean;
  stopTts: () => void;
  audioLevel: number;
  inputFrequencyData: Uint8Array | undefined;
  lastConversationId: string | null;
  dismissSummary: () => void;

  // UI toggles
  ttsEnabled: boolean;
  setTtsEnabled: (v: boolean) => void;
  agentMode: boolean;
  setAgentMode: (v: boolean) => void;
  showSessionList: boolean;
  setShowSessionList: (v: boolean) => void;

  // Document context (set when sidebar auto-opens from doc editor)
  docContext: { document_id: string; document_title?: string } | null;
  clearDocContext: () => void;

  // Contextual prompts
  suggestedPrompts: string[];
  showSuggestions: boolean;

  // Send with selection awareness
  handleSend: (content: string) => void;
}

const ChatSessionContext = createContext<ChatSessionContextValue | undefined>(undefined);

/**
 * Dynamic agent hint pool — prompts that surface real Martha capabilities.
 * Each hint references a genuine agent from public.agent_registry or a
 * built-in Talk tool. Hints are weighted-shuffled so the user sees fresh
 * suggestions each visit, biased toward route-relevant ones.
 */

interface AgentHint {
  text: string;
  /** Route prefix(es) where this hint is most relevant. Empty = global. */
  routes: string[];
  /** Weight 1-3: 3 = show more often */
  weight: number;
}

const DEFAULT_HINTS: AgentHint[] = [
  { text: 'Ask Developer Agent to review the latest PR for architecture issues', routes: ['/cr', '/bf'], weight: 3 },
  { text: 'Ask Knowledge Agent to find all specs related to the orchestrator', routes: ['/do'], weight: 3 },
  { text: 'Use Writer Agent to draft a spec from the requirements doc', routes: ['/do', '/me'], weight: 3 },
  { text: 'Ask Work Agent to show all blocked issues on this board', routes: ['/bo'], weight: 3 },
  { text: 'Use Work Agent to bulk-create stories from this epic breakdown', routes: ['/bo', '/ep'], weight: 3 },
  { text: 'Ask Work Agent to check sprint readiness across all epics', routes: ['/sp'], weight: 3 },
  { text: 'Use Content Agent to generate a LinkedIn post about our latest release', routes: ['/sm'], weight: 3 },
  { text: 'Ask Martha to generate a specification from this requirements doc', routes: ['/do', '/me'], weight: 3 },
  { text: 'Ask Martha to check build readiness — are all specs linked to tests?', routes: ['/sp', '/bf'], weight: 3 },
  { text: 'Ask MTP Pipeline Agent for the status of the latest fine-tuning run', routes: ['/ar/pi'], weight: 3 },
  { text: 'Ask Support Agent to triage incoming tickets and suggest responses', routes: ['/su'], weight: 3 },
  { text: 'Use Developer Agent to search the codebase for how we handle auth tokens', routes: ['/cr'], weight: 2 },
  { text: 'Ask Research Agent to deep-dive into how Temporal handles workflow versioning', routes: [], weight: 2 },
  { text: 'Ask Martha to score this document for completeness and quality', routes: ['/do', '/me'], weight: 2 },
  { text: 'Use Martha to search across all projects for anything about authentication', routes: [], weight: 2 },
  { text: 'Ask Martha to show recent activity — who changed what today?', routes: [], weight: 2 },
  { text: 'Ask Martha to kick off a sprint build for the current epics', routes: ['/sp'], weight: 2 },
  { text: 'Ask Martha to list open PRs that need review on the main repo', routes: ['/cr', '/bo'], weight: 2 },
  { text: 'Ask Martha to summarise the linked epics and their current status', routes: ['/ep', '/bo'], weight: 2 },
  { text: 'Use Knowledge Agent to find which epics are missing test specs', routes: ['/ep', '/bo'], weight: 2 },
  // Design Studio & Playground — ADL composition prompts
  { text: 'Design a settings page with theme, notifications, and billing sections', routes: ['/ar/dg', '/ar/pg'], weight: 4 },
  { text: 'Add a KpiStrip showing revenue, profit margin, and headcount', routes: ['/ar/dg', '/ar/pg'], weight: 4 },
  { text: 'Create a financial table with monthly P&L breakdown', routes: ['/ar/dg', '/ar/pg'], weight: 4 },
  { text: 'Build a report layout with header, KPI strip, and data table', routes: ['/ar/dg', '/ar/pg'], weight: 4 },
  { text: 'Add a settings nav with General, Security, Integrations, and Billing items', routes: ['/ar/dg', '/ar/pg'], weight: 3 },
  { text: 'Design an onboarding wizard with 4 steps', routes: ['/ar/dg', '/ar/pg'], weight: 3 },
  { text: 'Create a client dashboard with engagement score and recent activity', routes: ['/ar/dg', '/ar/pg'], weight: 3 },
  { text: 'Add a filter bar with date range, status dropdown, and search', routes: ['/ar/dg', '/ar/pg'], weight: 3 },
];

const HINTS_CACHE_KEY = 'martha_chat_hints_v2';
// v2: credentials + cache key bump
const HINTS_TTL_MS = 60 * 60 * 1000; // 1 hour

function getCachedHints(): AgentHint[] | null {
  try {
    const raw = sessionStorage.getItem(HINTS_CACHE_KEY);
    if (!raw) return null;
    const { hints, ts } = JSON.parse(raw);
    if (Date.now() - ts > HINTS_TTL_MS) {
      sessionStorage.removeItem(HINTS_CACHE_KEY);
      return null;
    }
    return hints;
  } catch {
    return null;
  }
}

function setCachedHints(hints: AgentHint[]): void {
  try {
    sessionStorage.setItem(HINTS_CACHE_KEY, JSON.stringify({ hints, ts: Date.now() }));
  } catch { /* quota exceeded — ignore */ }
}

/** Pick N weighted-random hints relevant to the current route. */
function pickHints(hints: AgentHint[], pathname: string, count = 2): string[] {
  const pathAfterWs = pathname.replace(/^\/w\/[^/]+/, '');

  // Score each hint: route-relevant ones get boosted
  const scored = hints.map(h => {
    const routeMatch = h.routes.length === 0 || h.routes.some(r => pathAfterWs.includes(r));
    const score = routeMatch ? h.weight * 2 : (h.routes.length === 0 ? h.weight : 0);
    return { ...h, score };
  });

  // Weighted shuffle: sort by score * random factor
  const shuffled = scored
    .map(h => ({ ...h, rand: h.score * (0.5 + Math.random()) }))
    .sort((a, b) => b.rand - a.rand);

  // Take top N, deduplicating only when an agent name is present
  // (page action prompts have no agent name and are allowed to co-exist)
  const seen = new Set<string>();
  const result: string[] = [];
  for (const h of shuffled) {
    if (result.length >= count) break;
    const agentKey = h.text.match(/([\w\s]+ Agent|Martha|Archie Platform)/)?.[1];
    if (agentKey && seen.has(agentKey)) continue;
    if (agentKey) seen.add(agentKey);
    result.push(h.text);
  }
  return result;
}

export function ChatSessionProvider({ children }: { children: ReactNode }) {
  const [isExpanded, setExpandedRaw] = useState(loadExpanded);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [agentMode, setAgentMode] = useState(false);
  const [showSessionList, setShowSessionList] = useState(false);
  const [docContext, setDocContext] = useState<{ document_id: string; document_title?: string } | null>(null);
  const [agentHints, setAgentHints] = useState<AgentHint[]>(() => getCachedHints() || DEFAULT_HINTS);

  const { projectId } = useProject();
  const location = useLocation();
  const { selectedText, clearSelection } = useChatSelection();

  const talk = useTalkSession(projectId);
  const voice = useVoiceChat();

  // Fetch hints from API (sessionStorage-cached, 1h TTL)
  useEffect(() => {
    if (getCachedHints()) return; // already cached and fresh
    const controller = new AbortController();
    fetch('/api/orchestration/chat-hints', { signal: controller.signal, credentials: 'include' })
      .then(r => r.json())
      .then(json => {
        const hints: AgentHint[] = json?.data?.hints;
        if (Array.isArray(hints) && hints.length > 0) {
          setAgentHints(hints);
          setCachedHints(hints);
        }
      })
      .catch(() => { /* network error — keep defaults */ });
    return () => controller.abort();
  }, []);

  // Re-roll hints on new session or route change (stable within a session+page)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const suggestedPrompts = useMemo(() => {
    // Prefer page-specific hints from the route registry (always contextual)
    const routeHints = getChatHintsForPath(location.pathname);
    if (routeHints.length > 0) {
      const shuffled = [...routeHints].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, 3);
    }
    // Fall back to DB/default hints with route scoring
    return pickHints(agentHints, location.pathname, 3);
  }, [agentHints, location.pathname, talk.sessionId]);

  // Show suggestions on every navigation, hide once the user sends a message from this page
  const prevPathnameRef = useRef(location.pathname);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);

  useEffect(() => {
    if (location.pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = location.pathname;
      setSuggestionsDismissed(false); // re-show on navigation
    }
  }, [location.pathname]);

  const showSuggestions = !suggestionsDismissed;

  const setExpanded = useCallback((v: boolean) => {
    saveExpanded(v);
    setExpandedRaw(v);
  }, []);

  const toggle = useCallback(() => {
    setExpanded(!isExpanded);
  }, [isExpanded, setExpanded]);

  // Listen for external expand requests (e.g. @martha button in CommentPopover)
  // Extended: accepts { document_id, document_title } detail from topic canvas full-screen mode
  useEffect(() => {
    const handleExpand = (e: Event) => {
      setExpanded(true);
      const detail = (e as CustomEvent<{ document_id?: string; document_title?: string }>).detail;
      if (detail?.document_id) {
        setDocContext({ document_id: detail.document_id, document_title: detail.document_title });
      }
    };
    window.addEventListener('martha:expand-chat', handleExpand);
    return () => window.removeEventListener('martha:expand-chat', handleExpand);
  }, [setExpanded]);

  // Auto-play TTS for assistant responses when ttsEnabled
  useEffect(() => {
    if (!ttsEnabled || talk.isStreaming || talk.messages.length === 0) return;
    const lastMsg = talk.messages[talk.messages.length - 1];
    if (lastMsg.role === 'assistant' && !lastMsg.isStreaming && lastMsg.content) {
      voice.playTts(lastMsg.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talk.messages.length, talk.isStreaming]);

  const handleSend = useCallback((content: string) => {
    setSuggestionsDismissed(true); // hide suggestions once user sends from this page
    const fullContent = selectedText ? `${content}\n\n[Selected text: ${selectedText}]` : content;
    if (talk.isStreaming) {
      // Queue message while Martha is responding
      talk.queueMessage(fullContent);
    } else {
      const opts = {
        ...(agentMode ? { agentMode: true } : {}),
        ...(docContext ? { pageContextOverride: { document_id: docContext.document_id } } : {}),
      };
      talk.sendMessage(fullContent, Object.keys(opts).length > 0 ? opts : undefined);
    }
    clearSelection();
  }, [agentMode, selectedText, talk, clearSelection]);

  const clearDocContext = useCallback(() => setDocContext(null), []);

  const value: ChatSessionContextValue = {
    isExpanded,
    setExpanded,
    toggle,
    messages: talk.messages,
    isStreaming: talk.isStreaming,
    sendMessage: talk.sendMessage,
    cancelStream: talk.cancelStream,
    sessionId: talk.sessionId,
    sessions: talk.sessions,
    loadSession: talk.loadSession,
    startNewSession: talk.startNewSession,
    archiveSession: talk.archiveSession,
    isLoadingSessions: talk.isLoadingSessions,
    queuedMessage: talk.queuedMessage,
    clearQueue: talk.clearQueue,
    voiceStatus: voice.voiceStatus,
    isVoiceActive: voice.isVoiceActive,
    startVoice: voice.startVoice as () => void,
    stopVoice: voice.stopVoice,
    playTts: voice.playTts as (text: string) => void,
    isTtsPlaying: voice.isTtsPlaying,
    stopTts: voice.stopTts,
    audioLevel: voice.audioLevel,
    inputFrequencyData: voice.inputFrequencyData,
    lastConversationId: voice.lastConversationId,
    dismissSummary: voice.dismissSummary,
    ttsEnabled,
    setTtsEnabled,
    agentMode,
    setAgentMode,
    showSessionList,
    setShowSessionList,
    docContext,
    clearDocContext,
    suggestedPrompts,
    showSuggestions,
    handleSend,
  };

  return (
    <ChatSessionContext.Provider value={value}>
      {children}
    </ChatSessionContext.Provider>
  );
}

export function useChatSession(): ChatSessionContextValue {
  const context = useContext(ChatSessionContext);
  if (!context) {
    throw new Error('useChatSession must be used within a ChatSessionProvider');
  }
  return context;
}
